import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import PlayTag from "../../elements/PlayTag";
import StatusBadge from "../../elements/StatusBadge";
import { CampaignTableRow } from "./campaignTableTypes";

export type SortCol = "name" | "start_date" | "end_date" | "spend" | "impressions" | "clicks" | "ctr";
export type SortDir = "ASC" | "DESC";

type CampaignsTableProps = {
  rows: CampaignTableRow[];
  sort: { col: SortCol; dir: SortDir };
  onSortChange: (col: SortCol) => void;
  onRowClick?: (campaignId: string) => void;
  selectedIds: string[];
  onToggleRow: (campaignId: string) => void;
  onToggleAll: () => void;
};

const TableWrap = styled("div")({
  marginBottom: 8,
});

const TableBox = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  borderRadius: 10,
  border: `1px solid ${theme.palette.brand.gray200}`,
  overflow: "hidden",
}));

const TableScroll = styled("div")({
  overflowX: "auto",
});

const Table = styled("table")({
  width: "100%",
  minWidth: 860,
  borderCollapse: "collapse",
  fontSize: 13,
});

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  "& th": {
    padding: "12px 16px",
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.brand.gray500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: theme.palette.brand.gray50,
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
    textAlign: "left",
    userSelect: "none",
  },
  "& th.alignRight": {
    textAlign: "right",
  },
  "& th.sortable": {
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.brand.gray700,
    },
  },
}));

const Tbody = styled("tbody")(({ theme }: { theme: Theme }) => ({
  "& tr": {
    borderBottom: `1px solid ${theme.palette.brand.gray100}`,
    cursor: "pointer",
    transition: "background 0.15s",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: theme.palette.brand.gray50,
    },
  },
  "& td": {
    padding: "14px 16px",
    color: theme.palette.brand.gray700,
  },
  "& td.alignRight": {
    textAlign: "right",
  },
}));

const CoralCheckbox = styled(Checkbox)(({ theme }: { theme: Theme }) => ({
  "&.Mui-checked": {
    color: theme.palette.brand.coral,
  },
  "&.MuiCheckbox-indeterminate": {
    color: theme.palette.brand.coral,
  },
}));

const SelectCell = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiCheckbox-root": {
    padding: 4,
  },
});

const NameText = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const NumericBold = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 700,
  color: theme.palette.brand.gray900,
}));

const NumericPrimary = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const SecondaryText = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
}));

const SortIndicator = styled("span")({
  marginLeft: 4,
  opacity: 0.7,
});

const formatDate = (isoString: string): string => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const CampaignsTable: FC<CampaignsTableProps> = ({
  rows,
  sort,
  onSortChange,
  onRowClick,
  selectedIds,
  onToggleRow,
  onToggleAll,
}) => {
  const indicator = (col: SortCol) => {
    if (sort.col !== col) return <SortIndicator>⇅</SortIndicator>;
    return <SortIndicator>{sort.dir === "ASC" ? "▲" : "▼"}</SortIndicator>;
  };

  const selectableRows = rows.filter((row) => !row.processing);
  const areAllSelected = selectableRows.length > 0 && selectableRows.every((row) => selectedIds.includes(row.id));
  const areSomeSelected = selectableRows.some((row) => selectedIds.includes(row.id));

  return (
    <TableWrap>
      <TableBox>
        <TableScroll>
          <Table>
            <Thead>
              <tr>
                <th style={{ width: 48 }}>
                  <SelectCell>
                    <CoralCheckbox
                      checked={areAllSelected}
                      indeterminate={areSomeSelected && !areAllSelected}
                      disabled={selectableRows.length === 0}
                      onChange={onToggleAll}
                      inputProps={{ "aria-label": "Select all campaigns on this page" }}
                    />
                  </SelectCell>
                </th>
                <th className="sortable" onClick={() => onSortChange("name")}>
                  Campaign{indicator("name")}
                </th>
                <th>Campaign Type</th>
                <th>Status</th>
                <th className="sortable" onClick={() => onSortChange("start_date")}>
                  Start Date{indicator("start_date")}
                </th>
                <th className="sortable" onClick={() => onSortChange("end_date")}>
                  End Date{indicator("end_date")}
                </th>
                <th className="alignRight sortable" onClick={() => onSortChange("spend")}>
                  Spend{indicator("spend")}
                </th>
                <th className="alignRight sortable" onClick={() => onSortChange("impressions")}>
                  Impressions{indicator("impressions")}
                </th>
                <th className="alignRight sortable" onClick={() => onSortChange("clicks")}>
                  Clicks{indicator("clicks")}
                </th>
                <th className="alignRight sortable" onClick={() => onSortChange("ctr")}>
                  CTR{indicator("ctr")}
                </th>
              </tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <tr key={row.id} onClick={() => onRowClick?.(row.id)}>
                  <td>
                    <SelectCell onClick={(event) => event.stopPropagation()}>
                      <Tooltip title={row.processing ? "Campaign is still being created" : ""}>
                        <span>
                          <CoralCheckbox
                            checked={selectedIds.includes(row.id)}
                            disabled={row.processing}
                            onChange={() => onToggleRow(row.id)}
                            inputProps={{ "aria-label": `Select ${row.name}` }}
                          />
                        </span>
                      </Tooltip>
                    </SelectCell>
                  </td>
                  <td>
                    <NameText>{row.name}</NameText>
                  </td>
                  <td>
                    <PlayTag label={row.campaignType} />
                  </td>
                  <td>
                    <StatusBadge status={row.status} variant={row.statusVariant} />
                  </td>
                  <td>
                    <SecondaryText>{formatDate(row.start_date)}</SecondaryText>
                  </td>
                  <td>
                    <SecondaryText>{formatDate(row.end_date)}</SecondaryText>
                  </td>
                  <td className="alignRight">
                    <NumericBold>{row.spend}</NumericBold>
                  </td>
                  <td className="alignRight">
                    <SecondaryText>{row.impressions}</SecondaryText>
                  </td>
                  <td className="alignRight">
                    <SecondaryText>{row.clicks}</SecondaryText>
                  </td>
                  <td className="alignRight">
                    <NumericPrimary>{row.ctr}</NumericPrimary>
                  </td>
                </tr>
              ))}
            </Tbody>
          </Table>
        </TableScroll>
      </TableBox>
    </TableWrap>
  );
};

export default CampaignsTable;
